import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Business-card-sized (85 × 54 mm) pocket "consent" card that ISVAs, IDVAs
 * and youth workers can hand out. Front-only for simplicity and cheap
 * printing. Contains the "consent looks like / is not" comparison — the
 * two lists survivors return to most often — plus a tiny QR pointing to
 * the full Consent guide.
 *
 * The card body is rendered off-screen unless one of the print modes is
 * triggered from the Consent page:
 *   tf-print-consent-card       →  a single card, centred on A4 for preview
 *   tf-print-consent-card-10-a4 →  10 cards, 2 × 5, on A4 portrait
 * Both modes preserve QR ink colour and add faint dashed cut-guides.
 */

const LOOKS_LIKE = [
  'Enthusiastic yes',
  'Freely given',
  'Awake &amp; sober enough',
  'Can be withdrawn',
];
const IS_NOT = [
  'Silence or freezing',
  'Yes under pressure',
  'Asleep or too drunk',
  'Absence of a &ldquo;no&rdquo;',
];

export function ConsentPocketBody({ url }) {
  const displayUrl = url.replace(/^https?:\/\//, '').replace(/\?src=.*$/, '');
  return (
    <div className="tf-pc-body">
      <p className="tf-pc-eyebrow">CONSENT · A QUICK GUIDE</p>
      <div className="tf-pc-cols">
        <div className="tf-pc-col tf-pc-col-yes">
          <p className="tf-pc-col-title">LOOKS LIKE</p>
          <ul>
            {LOOKS_LIKE.map((t) => (
              <li key={t} dangerouslySetInnerHTML={{ __html: `✓ ${t}` }} />
            ))}
          </ul>
        </div>
        <div className="tf-pc-col tf-pc-col-no">
          <p className="tf-pc-col-title">IS NOT</p>
          <ul>
            {IS_NOT.map((t) => (
              <li key={t} dangerouslySetInnerHTML={{ __html: `✗ ${t}` }} />
            ))}
          </ul>
        </div>
      </div>
      <div className="tf-pc-footer">
        <div className="tf-pc-qr">
          <QRCodeSVG
            value={url}
            size={68}
            level="M"
            includeMargin={false}
            bgColor="#ffffff"
            fgColor="#3F1F5B"
          />
        </div>
        <div className="tf-pc-caption">
          <p className="tf-pc-brand">The Full Impact</p>
          <p className="tf-pc-url">{displayUrl}/app/consent</p>
          <p className="tf-pc-scan">Scan for the full UK guide</p>
        </div>
      </div>
    </div>
  );
}

/** Single card wrapper (used in the 1-per-preview print mode). */
export default function ConsentCardPocket({ url }) {
  return (
    <aside
      className="tf-pc-single"
      aria-hidden="true"
      data-testid="consent-pocket-card"
    >
      <div className="tf-pc-card">
        <ConsentPocketBody url={url} />
      </div>
    </aside>
  );
}

/** Multi-card sheet — repeats the pocket card N times in a grid. */
export function ConsentCardPocketMulti({ url, count = 10, layout = '10-a4' }) {
  const cells = Array.from({ length: count });
  return (
    <aside
      className={`tf-pc-multi tf-pc-multi-${layout}`}
      aria-hidden="true"
      data-testid={`consent-pocket-multi-${layout}`}
    >
      {cells.map((_, i) => (
        <div key={i} className="tf-pc-card">
          <ConsentPocketBody url={url} />
        </div>
      ))}
    </aside>
  );
}
